import { NgClass } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Type, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { DropdownOption } from '@app/v2/shared/types/dropdown-options.type'
import { NgxTippyModule, NgxTippyProps } from 'ngx-tippy-wrapper'
import { Subject, takeUntil } from 'rxjs'
import { DropdownComponent } from '../../dropdown/dropdown.component'
import { BasicInputComponent } from '../basic-input/basic-input.component'

type ValueType = {
  id?: number | string
  name?: string
  label?: string
  place_name?: string
  flag?: string
  key?: number | string
  icon?: string
  disabled?: boolean
  /** Hover text for an option chip. Empty hides the tooltip. */
  tooltip?: string
  /** Gray chip. Used for archived events that cannot be submitted. */
  chipMuted?: boolean
  optionComponent?: Type<unknown>
  optionInputs?: Record<string, unknown>
}

@Component({
  selector: 'select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  standalone: true,
  imports: [BasicInputComponent, NgClass, DropdownComponent, NgxTippyModule]
})
export class SelectInputComponent implements OnInit, OnDestroy {
  @Input() id: string = ''
  // Form control
  @Input() control!: FormControl
  // values to show in select input
  @Input()
  set values(values: ValueType[] | string[]) {
    if (values && Array.isArray(values)) {
      this._values = values
      // Only set isDropdown automatically if it hasn't been explicitly set via @Input
      if (this.isDropdown === undefined) {
        this.isDropdown = values.length > 8 || this.isCityField
      }
      if (this.isDropdown) {
        this.filteredValues = this.values
        // Defer initial value->index mapping to ngOnInit so that all sibling inputs
        // (notably isSingleSelect) are bound first; otherwise multi-select inputs are
        // treated as single-select during this early call and only the last index survives.
        if (this._initialized && this.control?.value != null && this.control.value !== '') this.updateSelectedValues(this.control.value)
        this.setDropdownOptions()
      }
      this._Cdr.detectChanges()
    } else if (values && Array.isArray(values) && values.length === 0) {
      // Handle empty array case - still update the values
      this._values = values
      if (this.isDropdown) {
        this.filteredValues = this.values
        this.setDropdownOptions()
      }
      this._Cdr.detectChanges()
    }
  }
  // label to be shown in select input
  @Input() label!: string
  // Key to filter objects with
  @Input() key!: keyof ValueType
  // Key to choose value from objects with
  @Input() valueName!: string
  // Placeholder to be shown in select input
  @Input() placeholder: string = ''
  // Flag to show tooltip for values
  @Input() showTooltip: boolean = false
  // Flag for tooltip values if needed
  @Input() tooltipValues: string[] = []
  // Flag for tooltip placement
  @Input() tooltipPlacement: 'top' | 'bottom' | 'right' | 'left' = 'top'
  // Flag to show icon for select input
  @Input() icon!: string
  // Flag to identify iconType for select input
  @Input() iconType!: string
  // Flag to show required * for select input
  @Input() required: boolean = false
  // Flag to show clear icon for select input
  @Input() clear: boolean = false
  // Flag to show clear all icon for select input
  @Input() clearAll: boolean = false
  // Flag to show counter for select input
  @Input() counter: boolean = false
  // Flag to make input single or multiple select
  @Input() isSingleSelect: boolean = true
  // Flag for maximum number of selected values
  @Input() minSelections: number = 0
  // Flag for maximum number of selected values
  @Input() maxSelections!: number
  // Flag to enable search on input field
  @Input() enableSearch: boolean = false
  // Flag to enable country field
  @Input() isCountryField: boolean = false
  // Flag to apply normal selection style
  @Input() isBasicDropdownValue: boolean = false
  // Flag to apply auto complete attribute
  @Input() autocomplete: 'on' | 'off' | 'current-password' = 'off'
  // Flag to customize selected item and input icon
  @Input() basicSelect: boolean = false
  // Filtered values from outside component
  @Input() filteredData: ValueType[] | string[] = []
  // Flag to disable input field manually
  @Input() isDisabled: boolean = false
  // Flag to enable user to add custom value
  @Input() canAddCustomValue: boolean = false
  @Input() isDropdown?: boolean
  @Input() isCityField: boolean = false
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  @Input() optionNoData: string = ''
  @Input() fieldType: BasicFieldType = BasicFieldType.SELECT
  // Flag to defer form control updates until confirmed
  @Input() deferControlUpdate: boolean = false
  @Input() dropdownMaxHeightInPX: number = 170
  @Input() dropdownHeightInPX: number = 150
  /** Multi-select: this option clears others; picking others clears this (e.g. All events). */
  @Input() exclusiveOptionId: string | number | null = null
  /** True while the field shows the auto-change highlight. */
  @Input() autoChangeHighlight = false
  // External search query flag from parent components like city-input
  @Input() set hasSearchQueryExternal(value: boolean) {
    this.hasSearchQuery = value
  }
  // Emit value for parent element
  @Output() valueChanged: EventEmitter<ValueType[] | string[] | string | null> = new EventEmitter<ValueType[] | string[] | string | null>()
  // Emit when the input loses focus
  @Output() blurEvent: EventEmitter<void> = new EventEmitter<void>()
  // Emit when user tries to select beyond maxSelections (parent can show warning alert)
  @Output() maxSelectionsExceeded: EventEmitter<void> = new EventEmitter<void>()
  // Emit value in case country field
  @Output() onSearch: EventEmitter<string> = new EventEmitter()
  // Emit when user presses a key down
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @ViewChild('dropdownRef') dropdownRef!: DropdownComponent
  @ViewChild('selectedValuesContainer') selectedValuesContainer?: ElementRef<HTMLElement>
  /** Chip tooltip sits on document.body so the 4-row scroller does not clip it. */
  public readonly chipTippyProps: NgxTippyProps = {
    theme: 'dark',
    placement: 'top',
    appendTo: () => document.body
  }
  /** Option-chip tooltip. Above modals (z-1500) and not clipped by overflow. */
  public readonly optionChipTippyProps: NgxTippyProps = {
    theme: 'dark',
    placement: 'top',
    allowHTML: true,
    appendTo: () => document.body,
    maxWidth: 280,
    zIndex: 10000
  }
  public dropdown: boolean = false
  public valueHovered: number = 0
  public selectedValues: number[] = []
  public filteredValues: ValueType[] | string[] = []
  public selectedValue!: string
  public isFocused!: boolean
  public dropdownOptions!: DropdownOption[]
  public BasicInputMode = BasicInputMode
  public hasSearchQuery: boolean = false
  public forceHideDropdown: boolean = false
  // Length of search text for multi-select searchable
  private _searchInputLength: number = 0
  private _values: ValueType[] | string[] = []
  private _destroy$: Subject<void> = new Subject<void>()
  private _pendingValue: string = null
  private _initialized: boolean = false
  private static _idCounter = 0

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  get values(): any[] {
    return this._values
  }

  /** Width for multi-select search input: sizes to content (min 2ch). */
  get searchInputWidth(): string {
    // Keep placeholder fully visible when no selections/search text exist.
    if (!this.isSingleSelect && this.enableSearch && !this.hasValueForLabel && !this.hasSearchQuery) return '100%'
    const len = Math.max(1, this._searchInputLength)
    return `${len + 1}ch`
  }

  /** True when the field has at least one selection (so label should float). Empty array counts as no value. */
  get hasValueForLabel(): boolean {
    const v = this.control.value
    if (v == null || v === '') return false
    if (Array.isArray(v)) return v.length > 0
    return true
  }

  @ViewChild('selectInput') selectInput!: ElementRef<HTMLInputElement>

  /**
   * Gets the display value for the input field.
   * When user is actively searching or has focused the input, returns the current input value to allow editing.
   * For multi-select with search, the input is only for typing to filter — never show selected value (chips show that).
   */
  get inputDisplayValue(): string {
    // Multi-select with search: when no selection and not typing, show empty so placeholder stays visible when focused
    if (this.enableSearch && this.isDropdown && !this.isSingleSelect && !this.hasValueForLabel && !this.hasSearchQuery) return ''
    // For searchable dropdowns, when user is typing or has focused, use the current input value
    if (this.enableSearch && this.isDropdown && this.selectInput?.nativeElement) {
      if (this.isFocused || this.hasSearchQuery) {
        return this.selectInput.nativeElement.value
      }
      // Multi-select with search: input is for search only, keep empty so selected values show only as chips
      if (!this.isSingleSelect) return ''
    }
    // Single-select / non-search: show selected value or empty
    return (this.isCountryField || this.isBasicDropdownValue) && this.selectedValue && !this.hasSearchQuery ? this.selectedValue : ''
  }
  @ViewChild('dropdown')
  dropdownElement!: ElementRef<HTMLDivElement>
  @ViewChild('container') container!: ElementRef<HTMLDivElement>

  /**
   * @description Listen to closing the dropdown event
   */
  public onDropdownClosed(): void {
    setTimeout(() => {
      this._checkIfValueExists()
      this.control.markAsTouched()
      this.dropdown = false
      this.isFocused = false
      this.hasSearchQuery = false
      if (!this.isSingleSelect && this.enableSearch && this.selectInput?.nativeElement) {
        this.selectInput.nativeElement.value = ''
      }
    })
  }

  constructor(private _Sanitizer: DomSanitizer, private _Cdr: ChangeDetectorRef) {}

  /**
   * Initializes component
   * @returns void
   */
  ngOnInit(): void {
    // Generate unique ID if not provided
    if (!this.id) this.id = `select-input-${SelectInputComponent._idCounter++}`
    if (this.isDisabled) this.control.disable()
    this._initialized = true
    this._initializeSelection()
    this.control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((newVal) => {
      if (!newVal && newVal !== 0) this.resetSelection()
      else this.updateSelectedValues(newVal)
    })
  }

  /**
   * Initializes the selection based on the control value
   * @returns void
   */
  private _initializeSelection(): void {
    this.maxSelections = this.maxSelections || this.values.length
    if (this.maxSelections === 1) this.isSingleSelect = true
    if (this.control.value || this.control.value === 0) {
      if (!this.canAddCustomValue) {
        const validValues = new Set(
          this.values.filter((value) => {
            const controlValue = this.control.value
            // Check if the value is an array
            if (Array.isArray(controlValue)) {
              // Check if the control value is an array and if the value is in the control value array
              return controlValue.some((controlValue) => this._compareValues(controlValue, value))
            }

            // Check if the value is not an array and if the value is equal to the control value
            return this._compareValues(controlValue, value)
          })
        )

        if (!Array.isArray(this.control.value)) {
          const selectedValue = validValues.values().next().value
          if (selectedValue) this.selectValue(selectedValue, 'init')
          else if (!selectedValue && !this.values?.length) this.selectedValue = this.control.value
          else this.control.reset()
        } else {
          this.control.value.forEach((v: any) => {
            const validValue = Array.from(validValues).find((value: any) => (typeof value === 'object' && value !== null ? value.id === v : value === v))
            if (validValue) this.selectValue(validValue, 'init')
          })
        }
      }
    }
  }

  /**
   * Compares two values, with special handling for strings and numbers.
   * @param controlValue - The value from control.
   * @param valueToCheck - The value to check against the control value.
   * @returns boolean - True if the values match, false otherwise.
   */
  private _compareValues(controlValue: any, valueToCheck: any): boolean {
    if (typeof controlValue === 'string' && typeof valueToCheck === 'string') {
      return valueToCheck.toLowerCase() === controlValue.toLowerCase()
    }

    if (typeof controlValue === 'string' && typeof valueToCheck === 'object') {
      const value = valueToCheck[(this.valueName ?? 'id') || this.key] || valueToCheck
      return value === controlValue
    }

    if (typeof controlValue === 'number') {
      const value = typeof valueToCheck === 'object' && valueToCheck !== null ? valueToCheck[(this.valueName ?? 'id') || this.key] : valueToCheck
      return value === controlValue
    }

    return false
  }

  /**
   * Resets the selected values and marks the control as untouched and pristine
   * @returns void
   */
  private resetSelection(): void {
    this.control.markAsPristine()
    this.selectedValues = []
    // Reset filteredValues to show all available values when selection is cleared
    this.filteredValues = this.values
    this._Cdr.detectChanges()
  }

  private _checkIfValueExists(): void {
    if (!this.selectInput?.nativeElement?.value && this.isSingleSelect) {
      this.clearAllValues()
      this.selectedValue = ''
    }
  }

  /**
   * Updates the selected values based on the new value of the control
   * @param newVal any - The new value of the control
   * @returns void
   */
  private updateSelectedValues(newVal: any): void {
    if (this.isCountryField && typeof newVal === 'object' && newVal !== null) this.selectedValue = newVal.name || newVal.place_name || ''
    else
      this.selectedValue =
        this.isCountryField || this.valueName === 'id' ? this.values.find((v) => v['id'] != null && String(v['id']) === String(newVal))?.name || '' : this.control.value

    const selectedIndices = new Set<number>()

    if (Array.isArray(newVal)) {
      newVal.forEach((val: any) => {
        const index = this.findIndexOfSelectedValue(val)
        if (index !== -1) selectedIndices.add(index)
      })
    } else {
      const index = this.findIndexOfSelectedValue(newVal)
      if (index !== -1) selectedIndices.add(index)
    }
    this.selectedValues = this.isSingleSelect && selectedIndices.size > 0 ? [Array.from(selectedIndices).pop() as number] : Array.from(selectedIndices)
    if (this.isDropdown) this.setDropdownOptions()
    this._Cdr.markForCheck()
  }

  /**
   * sanitize SVG icon to be rendered in template
   * @param icon string
   * @returns SafeHtml
   */
  public sanitizeSVG(icon: string): SafeHtml {
    return this._Sanitizer.bypassSecurityTrustHtml(icon)
  }

  /**
   * Find original index of selected value from the non mutated values array
   * @param selectedValue any
   * @returns number
   */
  private findIndexOfSelectedValue(selectedValue: any): number {
    // Handle country objects specifically
    if (this.isCountryField && typeof selectedValue === 'object' && selectedValue !== null) {
      return this.values.findIndex((val) => {
        if (typeof val === 'object' && val !== null) return val.id === selectedValue.id
        return false
      })
    }

    const selectedValueKey = this.valueName !== '{}' ? selectedValue[this.valueName ?? this.key] ?? selectedValue : selectedValue[this.key ?? this.valueName] ?? selectedValue

    return this.values.findIndex((val, index) => {
      // Extract the comparison key from the array item
      // For payment gateways: we want to compare the 'name' field (valueName) with the extracted selectedValueKey
      const valueKey = this.isCountryField || this.valueName === 'id' ? val['id'] : val[this.valueName ?? this.key] ?? val

      if (typeof valueKey === 'number' && typeof selectedValueKey === 'number') return valueKey === selectedValueKey
      if (valueKey != null && selectedValueKey != null) {
        return String(valueKey).toLowerCase() === String(selectedValueKey).toLowerCase()
      }
      return JSON.stringify(selectedValue) === JSON.stringify(val)
    })
  }

  /**
   * Is value selected
   * @param value any
   * @returns boolean
   */
  public isSelected(selectedValue: any): boolean {
    const index = this.findIndexOfSelectedValue(selectedValue)
    return this.selectedValues?.includes(index)
  }

  /**
   * clear value that was selected before
   * @param selectedValue any
   * @returns void
   */
  public clearValueFromSelectedValues(selectedValue: ValueType | string): void {
    if (this.control.disabled) return
    const index = this.findIndexOfSelectedValue(selectedValue)

    if (this.isSingleSelect) {
      this.selectedValues = []
      this.hasSearchQuery = false
      // Emit empty string for city fields, null for others
      this.control.markAsDirty()
      const emittedValue = this.isCityField ? '' : null
      this.valueChanged.emit(emittedValue)

      // Store pending value if deferControlUpdate is enabled
      if (this.deferControlUpdate) this._pendingValue = emittedValue
      else this._setControlValue(emittedValue)
      return
    }

    const newIndex = this.selectedValues.findIndex((v) => v === index)
    this.selectedValues.splice(newIndex, 1)
    const value = this.selectedValues.map((i) => this.values[i]).length ? this.selectedValues.map((i) => this.values[i]) : null

    // Reset search query flag and clear input when no values are selected (so placeholder shows)
    if (!value) {
      this.hasSearchQuery = false
      this._searchInputLength = 0
      if (this.selectInput?.nativeElement) this.selectInput.nativeElement.value = ''
    }

    const emittedValue = value && (this.isSingleSelect ? value[0][this.valueName ?? this.key] ?? value[0] : value.map((v) => v[this.valueName ?? this.key] ?? v))
    this.control.markAsDirty()
    this.valueChanged.emit(emittedValue)

    // Store pending value if deferControlUpdate is enabled
    if (this.deferControlUpdate) this._pendingValue = emittedValue
    else this._setControlValue(emittedValue)

    if (this.isDropdown) this.setDropdownOptions()
    if (!this.isSingleSelect && this.dropdown) this._afterMultiSelectChange()
  }

  /** Chip label shown in the tooltip. Same text as the chip. */
  public chipTooltip(index: number): string | null {
    const item = this.values?.[index]
    if (item == null || item === '') return null
    const label = typeof item === 'object' ? item[this.key] : item
    if (label == null || label === '' || typeof label === 'object') return null
    return String(label)
  }

  /** Keep latest chips in view and glue the open panel to the trigger. */
  private _afterMultiSelectChange(): void {
    setTimeout(() => {
      const container = this.selectedValuesContainer?.nativeElement
      if (container) container.scrollTop = container.scrollHeight
      if (this.dropdown) this.dropdownRef?.reposition()
    })
  }

  /**
   * Clear all selected values and reset input
   * @returns void
   */
  public clearAllValues(): void {
    if (this.isDropdown) this.hideDropdown()
    this.selectedValues = []
    this.setDropdownOptions(false)
    this.filteredValues = this.values
    if (this.selectInput) this.selectInput.nativeElement.value = ''
    this.hasSearchQuery = false
    this.control.markAsDirty()
    // Emit empty string for city fields, null for others
    const emittedValue = this.isCityField ? '' : null
    this.valueChanged.emit(emittedValue)

    // Store pending value if deferControlUpdate is enabled
    if (this.deferControlUpdate) this._pendingValue = emittedValue
    else this._setControlValue(emittedValue)
  }

  /**
   * Writes the control value. Empty clear on a required field is touched so the required error shows.
   * setValue runs valueChanges first, and that path marks the control pristine.
   */
  private _setControlValue(value: unknown): void {
    this.control.setValue(value)
    const empty = value == null || value === '' || (Array.isArray(value) && value.length === 0)
    if (empty && this.required) this.control.markAsTouched()
  }

  /**
   * select value and emit it to parent component
   * @param selectedValue any
   * @returns void
   */
  public selectValue(selectedValue: ValueType | string, identifier?: string): void {
    if (this.control.disabled && !identifier) return
    if (!identifier && !this.isDropdown && typeof selectedValue === 'object' && selectedValue?.disabled) return
    const index = this.findIndexOfSelectedValue(this.isCountryField || this.valueName === 'id' ? (selectedValue as ValueType)['id'] : selectedValue)

    if (!identifier) {
      this.control.markAsTouched()
      this.control.markAsDirty()
    }
    this.resetSelectInput()
    this.dropdown = false
    this.isFocused = false

    if (this.isSelected(selectedValue) && !this.isCountryField) {
      if (this.clear) return
      return this.clearValueFromSelectedValues(selectedValue)
    }

    if (index === -1) return
    if (!this.isDropdown) this.filteredValues = this.values

    if (!this.isSingleSelect && this.exclusiveOptionId != null) {
      const exclusiveIndex = this._findExclusiveOptionIndex()
      const isSelectingExclusive = exclusiveIndex !== -1 && index === exclusiveIndex

      if (isSelectingExclusive) {
        this.selectedValues = []
      } else if (exclusiveIndex !== -1 && this.selectedValues.includes(exclusiveIndex)) {
        this.selectedValues = this.selectedValues.filter((selectedIndex) => selectedIndex !== exclusiveIndex)
      }
    }

    if (!this.isSingleSelect && this.selectedValues.length >= this.maxSelections) {
      this.maxSelectionsExceeded.emit()
      return
    }

    this.isSingleSelect ? (this.selectedValues = [index]) : this.selectedValues.push(index)
    const value = this.isSingleSelect ? [this.filteredValues[index] ?? this.values[index]] : this.selectedValues.map((i) => this.values[i])
    const emittedValue = this.isSingleSelect ? value[0][this.valueName ?? this.key] ?? value[0] : value.map((v) => v[this.valueName ?? this.key] ?? v)

    this.selectedValue = value[0]?.name || value[0]?.place_name
    if (this.isSingleSelect && this.isDropdown && this.selectInput?.nativeElement) this.selectInput.nativeElement.value = this.selectedValue

    // Store pending value if deferControlUpdate is enabled
    if (this.deferControlUpdate) this._pendingValue = emittedValue
    else this.control.setValue(emittedValue)

    this.valueChanged.emit(emittedValue)
    if (this.isDropdown) this.setDropdownOptions()
    if (!identifier && !this.isSingleSelect) this._afterMultiSelectChange()
  }

  private _findExclusiveOptionIndex(): number {
    if (this.exclusiveOptionId == null) return -1
    return this.values.findIndex((val) => {
      const valueKey = typeof val === 'object' && val !== null ? val[this.valueName ?? 'id'] ?? val.id : val
      return valueKey === this.exclusiveOptionId
    })
  }

  /**
   * Remove value from x icon on badge
   * @param selectedValue any
   * @param event Event
   * @returns void
   */
  public removeValue(selectedValue: any, event: Event): void {
    if (this.control.disabled) return
    event.stopPropagation()
    this.dropdown = false
    if (this.isSelected(selectedValue)) {
      this.clearValueFromSelectedValues(selectedValue)
      this.blurEvent.emit()
    }
  }

  /**
   * Forward keydown to parent; for multi-select also remove last chip on Backspace/Delete when input empty.
   * @param event KeyboardEvent
   */
  public onInputKeydown(event: KeyboardEvent): void {
    this.onKeydown.emit(event)
    if (this.control.disabled || this.isSingleSelect) return
    const inputEl = this.selectInput?.nativeElement as HTMLInputElement
    const isEmpty = !inputEl?.value?.trim()
    if ((event.key === 'Backspace' || event.key === 'Delete') && isEmpty && this.selectedValues?.length > 0) {
      event.preventDefault()
      const lastIndex = this.selectedValues[this.selectedValues.length - 1]
      const lastValue = this.values[lastIndex]
      if (lastValue != null) this.clearValueFromSelectedValues(lastValue)
    }
  }

  /**
   * Show dropdown
   * @returns void
   */
  public showDropdown(): void {
    this.isFocused = true
    if (this.control.disabled) return

    // Ensure dropdown options are up to date when opening
    if (this.isDropdown) {
      this.filteredValues = this.values
      this.setDropdownOptions()
    }

    // For city fields, reset everything when dropdown is opened to provide clean search experience
    if (this.isCityField) {
      // Clear previous search state
      this.hasSearchQuery = false
      this.filteredValues = []
      this.forceHideDropdown = true
      this.setDropdownOptions(false)
      // Keep the selected value in the input
      if (this.selectInput) this.selectInput.nativeElement.value = this.selectedValue || ''
      // Update dropdown with current state
      if (this.dropdownRef) this.dropdownRef.forceHide = this.forceHideDropdown
      return
    } else {
      // For non-city fields, reset filtered values to show all options when dropdown is opened
      this.forceHideDropdown = false
      if (this.control.value && !this.hasSearchQuery) {
        this.filteredValues = this.values
        this.setDropdownOptions()
      }
    }

    if (!this.dropdown) this.dropdownRef.openDropdown(new Event('focus'))
    this.dropdown = true

    setTimeout(() => {
      const selectedItem = this.dropdownElement?.nativeElement?.querySelector('.selected')
      selectedItem?.scrollIntoView({ behavior: 'instant', block: 'nearest' })
      this.valueHovered = this.selectedValues[0]
    }, 100)

    // Find first unselected value more efficiently
    const firstUnselectedIndex = this.values.findIndex((_, i) => !this.selectedValues.includes(i))
    if (firstUnselectedIndex !== -1) {
      this.valueHovered = firstUnselectedIndex
      this.scrollToSelection()
    }
  }

  /**
   * Hide dropdown
   * @returns void
   */
  public hideDropdown(): void {
    if (this.selectInput?.nativeElement) this.selectInput.nativeElement.blur()
    this.dropdown = false
    this.isFocused = false
    if (this.dropdownRef) this.dropdownRef.close()
  }

  /**
   * Scroll to selection when using keyboard arrows
   * @returns void
   */
  private scrollToSelection(): void {
    if (!this.dropdownElement?.nativeElement) return

    const dropdown = this.dropdownElement.nativeElement
    const item = dropdown.querySelectorAll('.dropdown__selection')[this.valueHovered] as HTMLElement
    if (item) {
      const itemTop = item.offsetTop
      const itemBottom = itemTop + item.offsetHeight
      const dropdownTop = dropdown.scrollTop
      const dropdownBottom = dropdownTop + dropdown.offsetHeight

      if (itemTop < dropdownTop) {
        dropdown.scrollTop = itemTop
      } else if (itemBottom > dropdownBottom) {
        dropdown.scrollTop = itemBottom - dropdown.offsetHeight
      }
    }
  }

  /**
   * Filter values when typing in input field
   * @param event Event
   * @returns void
   */
  public filterValues(event: Event): void {
    this.valueHovered = 0
    const inputElement = event.target as HTMLInputElement
    const originalValue = inputElement.value
    const value = originalValue.toLowerCase()

    // Track if user has entered a search query
    // Also clear selectedValue when input becomes empty to prevent it from reappearing
    this.hasSearchQuery = !!value
    if (!value && this.enableSearch && this.isDropdown) {
      // Clear selectedValue when user clears the input to prevent it from reappearing
      this.selectedValue = ''
    }
    if (this.enableSearch && !this.isSingleSelect) this._searchInputLength = originalValue.length

    const newFilteredValues = value
      ? this.values.filter((v) => {
          const key = v[this.key] || v
          const isMatch = key.toLowerCase().includes(value)

          if (this.isCountryField) {
            const alternatives = v['alternatives'] || []
            const arabicName = v['arabic_name'] || ''
            return isMatch || alternatives.includes(value) || arabicName.includes(value)
          }

          return isMatch
        })
      : []
    // When search is cleared (empty value), reset filteredValues to show all values for dropdown fields
    // For city fields, keep empty array to trigger API call
    if (!value && this.isDropdown && !this.isCityField) {
      this.filteredValues = this.values
    } else {
      this.filteredValues = !newFilteredValues.length && !this.isCityField && !value ? this.values : newFilteredValues
    }
    this.setDropdownOptions()
    // For searchable dropdowns, don't clear control value when user is typing
    // This allows the user to edit the input value without it being cleared
    // Only clear control value for non-searchable dropdowns or when not actively typing
    if (!(this.enableSearch && this.isDropdown)) {
      this.control.setValue(null)
    }

    // For city fields, manage dropdown visibility based on whether there are options
    if (this.isCityField) {
      // Show dropdown only if there are filtered values or if user has searched and no results (to show message)
      const shouldShowDropdown = this.filteredValues.length > 0 || (this.hasSearchQuery && this.filteredValues.length === 0)
      this.forceHideDropdown = !shouldShowDropdown

      // Show dropdown when user starts typing and there are results or should show message
      if (value && !this.dropdown && shouldShowDropdown) {
        if (!this.dropdown) this.dropdownRef.openDropdown(new Event('focus'))
        this.dropdown = true
      }
    }

    // Ensure dropdown is updated with current hasSearchQuery and forceHide state
    if (this.dropdownRef) {
      this.dropdownRef.hasSearchQuery = this.hasSearchQuery
      this.dropdownRef.forceHide = this.forceHideDropdown
    }

    for (let i = 0; i < this.filteredValues.length; i++) {
      if (!this.selectedValues.includes(i)) {
        this.valueHovered = i
        this.scrollToSelection()
        // Emit search with original case-preserved value before returning
        this.onSearch.emit(originalValue)
        return
      }
    }

    if (!value && this.isCountryField) {
      this.selectedValues.splice(0)
      this.control.reset()
    }
    // Emit search with original case-preserved value
    this.onSearch.emit(originalValue)
  }

  /**
   * @description Set the dropdown options from the filtered values
   */
  public setDropdownOptions(allowDisabled: boolean = true) {
    this.dropdownOptions = this.filteredValues?.map((value: ValueType) => {
      let prefixIcon: string | undefined = undefined

      // Check for flag (country field)
      if (value?.flag) {
        prefixIcon = `<img src="${value?.flag}" style="height: 14px;width: 22px;border-radius: 25%;"/>`
      }
      // Check for icon property (custom icons like organization logos or event status dots)
      else if (value?.icon) {
        prefixIcon = value.icon
      }

      const valueIndexInValues = this.findIndexOfSelectedValue(value)
      const isSelected = valueIndexInValues !== -1 && (this.isSingleSelect ? this.selectedValues[0] === valueIndexInValues : this.selectedValues.includes(valueIndexInValues))
      const disabledByValue = !!value?.disabled
      const disabledBySelection = allowDisabled && isSelected
      const disabled = disabledByValue || disabledBySelection
      const optionComponent = value?.optionComponent

      return {
        value: value?.name || value.place_name || value,
        key: value?.key || value?.id || value?.name,
        disabled,
        prefixIcon,
        data: optionComponent
          ? {
              optionComponent,
              inputs: value.optionInputs ?? {}
            }
          : undefined
      }
    }) as DropdownOption[]
  }

  /**
   * reset selection input
   * @returns void
   */
  public resetSelectInput(): void {
    this.isFocused = false
    if (this.control.disabled) return
    if (!this.isCountryField && this.selectInput?.nativeElement) this.selectInput.nativeElement.value = ''
    this.filteredValues = this.values
    // Reset search query flag when input is reset
    if (!this.control.value) this.hasSearchQuery = false
  }

  /**
   * Get counter label
   * @returns void
   */
  get counterLabel(): string {
    return `${this.selectedValues.length}/${this.minSelections > 1 ? (this.minSelections !== this.maxSelections ? `${this.minSelections}~` : '') : ''}${this.maxSelections}`
  }
  /**
   * Add custom value
   * @returns void
   */
  public addCustomValue(): void {
    if (!this.selectInput?.nativeElement) return
    const value = this.selectInput.nativeElement.value
    this.values.push(typeof this.values[0] === 'string' ? value : { [this.key]: value })
    this.selectValue(value)
    this.resetSelectInput()
    this.hideDropdown()
    return
  }

  /**
   * Get dropdown container height
   * @returns number
   */
  get dropdownContainerHeight(): number {
    return this.container?.nativeElement?.offsetHeight
  }

  /**
   * @description Listen to blur event on the input and remove the focus
   */
  public onBlur(): void {
    this.isFocused = false

    // reset the selected value and the input value when nothing selected (empty array or null)
    const isEmpty = this.control.value == null || (Array.isArray(this.control.value) && this.control.value.length === 0)
    if (isEmpty) {
      this.selectedValue = ''
      if (this.enableSearch && !this.isSingleSelect) this._searchInputLength = 0
      if (this.selectInput?.nativeElement) this.selectInput.nativeElement.value = ''
      this.filteredValues = this.values
      this.hasSearchQuery = false // Reset search query flag when input is cleared
    }
    this.blurEvent.emit()
    this._Cdr.detectChanges()
  }

  /**
   * @description Handle the selected option when click on the option
   * @param event DropdownOption
   */
  public optionClicked(event: DropdownOption): void {
    this.selectValue({
      ...event,
      id: event.key,
      name: event.value,
      place_name: event.value
    })
    this.blurEvent.emit()
  }

  /**
   * @description Listen to the dropdown opened event
   */
  public onDropdownOpened(): void {
    this.dropdown = true
  }

  /**
   * @description Handles click on dropdown box to open dropdown
   * @param event MouseEvent
   */
  public onDropdownBoxClick(event: MouseEvent): void {
    // Stop propagation to prevent the dropdown toggle from firing
    event.stopPropagation()

    // Ensure dropdown options are up to date when opening
    if (this.isDropdown) {
      this.filteredValues = this.values
      this.setDropdownOptions()
    }

    // Focus the input field when clicking anywhere in the dropdown box
    if (this.selectInput?.nativeElement && !this.control.disabled) {
      this.selectInput.nativeElement.focus()
      // Position cursor at the end of the text
      this._positionCursorAtEnd()
    }

    if (!this.dropdown) this.dropdownRef.openDropdown(new Event('focus'))
  }

  /**
   * @description Positions the cursor at the end of the input text
   */
  private _positionCursorAtEnd(): void {
    if (this.selectInput?.nativeElement?.value) {
      const length = this.selectInput.nativeElement.value.length
      this.selectInput.nativeElement.setSelectionRange(length, length)
    }
  }

  /**
   * @description Confirms the pending value and updates the form control
   */
  public confirmPendingValue(): void {
    if (this.deferControlUpdate) {
      this.control.setValue(this._pendingValue)
      this._pendingValue = null
    }
  }

  /**
   * @description Cancels the pending value and resets to original state
   */
  public cancelPendingValue(): void {
    if (this.deferControlUpdate) {
      this._pendingValue = null
      // Reset the UI to show the current control value
      this._initializeSelection()
    }
  }
}
